import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';

type ExpirationState = 'valid' | 'warning' | 'expired';

function getExpirationState(expiresOn: string | null): ExpirationState {
  if (!expiresOn) return 'valid';
  const now = new Date();
  const expires = new Date(expiresOn);
  if (expires <= now) return 'expired';
  // Warning if less than 30 days remaining
  const daysRemaining =
    (expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  if (daysRemaining <= 30) return 'warning';
  return 'valid';
}

const stateConfig = {
  valid: {
    icon: CheckCircle2,
    colorClass: 'text-green-600 dark:text-green-400',
  },
  warning: {
    icon: AlertTriangle,
    colorClass: 'text-yellow-600 dark:text-yellow-400',
  },
  expired: {
    icon: XCircle,
    colorClass: 'text-red-600 dark:text-red-400',
  },
} as const;

interface ExpirationStatusProps {
  expiresOn: string | null;
  compact?: boolean;
}

export function ExpirationStatus({
  expiresOn,
  compact,
}: ExpirationStatusProps) {
  if (!expiresOn) {
    return (
      <span className="text-muted-foreground">
        {compact ? '—' : 'No expiration'}
      </span>
    );
  }

  const state = getExpirationState(expiresOn);
  const { icon: Icon, colorClass } = stateConfig[state];
  const relative = formatDistanceToNow(new Date(expiresOn), {
    addSuffix: true,
  });

  return (
    <span className={cn('inline-flex items-center gap-1.5', colorClass)}>
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className={compact ? '' : 'text-sm'}>{relative}</span>
    </span>
  );
}
